import { useCallback } from 'react';
import type {
  ProxyStartResponse,
  ElectronAPIResponse,
  DialogOptions,
  BrowserLaunchConfig,
  BrowserInjectCredentialsConfig,
  MtlsEntry,
  ModifiedRequest,
  ProxyStartConfig,
  ProxyStatusResponse,
  SystemProxyStatusResponse,
  CertificateInfoResponse,
  FileReadResponse,
  InstallCertificateResponse,
} from '../electron/proxy-types';
import { getCurrentHistoryTarget } from '../utils/current-history-target';

export const useElectronAPI = () => {
  // Check if running in Electron environment
  const isElectron = useCallback(() => {
    return typeof window !== 'undefined' && window.electronAPI;
  }, []);

  const startProxy = useCallback(
    async (config: ProxyStartConfig): Promise<ProxyStartResponse> => {
      if (!isElectron()) {
        return {
          success: false,
          error: 'Electron API not available',
        };
      }

      try {
        const result = await window.electronAPI.proxy.start(config);

        return result;
      } catch (error) {
        console.error('Failed to start proxy:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const stopProxy = useCallback(async (): Promise<ElectronAPIResponse> => {
    if (!isElectron()) {
      return {
        success: false,
        error: 'Electron API not available',
      };
    }

    try {
      const result = await window.electronAPI.proxy.stop();

      return result;
    } catch (error) {
      console.error('Failed to stop proxy:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [isElectron]);

  const getProxyStatus = useCallback(async (): Promise<ProxyStatusResponse> => {
    if (!isElectron()) {
      console.log('Mock: Getting proxy status');

      // Mock proxy status for browser development
      return {
        success: true,
        data: {
          isRunning: false,
          port: null,
          httpsPort: null,
        },
      };
    }

    try {
      const result = await window.electronAPI.proxy.getStatus();

      return {
        success: true,
        data: {
          isRunning: result.isRunning,
          port: result.port ?? null,
          httpsPort: result.httpsPort ?? null,
        },
      };
    } catch (error) {
      console.error('Failed to get proxy status:', error);

      return {
        success: false,
        data: {
          isRunning: false,
          port: null,
          httpsPort: null,
        },
        error: String(error),
      };
    }
  }, [isElectron]);

  const toggleIntercept = useCallback(
    async (enabled: boolean): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Toggling intercept:', enabled);

        // Mock successful intercept toggle for browser development
        return { success: true };
      }

      try {
        const result = await window.electronAPI.intercept.toggle(enabled);

        return result;
      } catch (error) {
        console.error('Failed to toggle intercept:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const setMtlsConfig = useCallback(
    async (entries: MtlsEntry[]): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        return { success: true };
      }

      try {
        return await window.electronAPI.proxy.setMtlsConfig(entries);
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const bindInstanceMtls = useCallback(
    async (payload: {
      instanceId: string;
      entry: MtlsEntry;
    }): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        return { success: true };
      }

      try {
        return await window.electronAPI.proxy.bindInstanceMtls(payload);
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const unbindInstanceMtls = useCallback(
    async (instanceId: string): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        return { success: true };
      }

      try {
        return await window.electronAPI.proxy.unbindInstanceMtls(instanceId);
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const setupTunInterface =
    useCallback(async (): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        return {
          success: false,
          error: 'Electron API not available',
        };
      }

      try {
        const result = await window.electronAPI.tun.setup();

        return result;
      } catch (error) {
        console.error('Failed to setup TUN interface:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  const getRequests = useCallback(async (): Promise<unknown[]> => {
    if (!isElectron()) {
      console.warn('Electron API not available - cannot get requests');

      return [];
    }

    try {
      const result = await window.electronAPI.requests.get();

      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Failed to get requests:', error);

      return [];
    }
  }, [isElectron]);

  const clearRequests = useCallback(async (): Promise<ElectronAPIResponse> => {
    if (!isElectron()) {
      return {
        success: false,
        error: 'Electron API not available',
      };
    }

    try {
      const result = await window.electronAPI.requests.clear(
        getCurrentHistoryTarget(),
      );

      return result;
    } catch (error) {
      console.error('Failed to clear requests:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [isElectron]);

  const exportRequests = useCallback(
    async (format: 'json' | 'har' | 'csv'): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Exporting requests in format:', format);

        // Mock successful export for browser development
        return { success: true };
      }

      try {
        const result = await window.electronAPI.requests.export(format);

        return result;
      } catch (error) {
        console.error('Failed to export requests:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const sendMessage = useCallback(
    (channel: string, ...args: unknown[]): void => {
      if (!isElectron()) {
        console.log('Mock: Sending message:', channel, args);

        return;
      }

      // Note: send method not available in current electronAPI interface
      console.log('Send message:', channel, args);
    },
    [isElectron],
  );

  const addEventListener = useCallback(
    (channel: string, callback: (...args: unknown[]) => void): void => {
      if (!isElectron()) {
        console.log('Mock: Adding event listener:', channel);

        return;
      }

      window.electronAPI.on(channel, callback);
    },
    [isElectron],
  );

  const removeEventListener = useCallback(
    (channel: string, callback: (...args: unknown[]) => void): void => {
      if (!isElectron()) {
        console.log('Mock: Removing event listener:', channel);

        return;
      }

      window.electronAPI.removeListener(channel, callback);
    },
    [isElectron],
  );

  const getAppInfo = useCallback(() => {
    if (!isElectron()) {
      console.log('Mock: Getting app info');

      // Mock app info for browser development
      return {
        version: '1.0.0',
        platform: 'browser',
        arch: 'x64',
      };
    }

    return {
      version: window.electronAPI.app.getVersion(),
      platform: window.electronAPI.app.getPlatform(),
      arch: window.electronAPI.app.getArch(),
    };
  }, [isElectron]);

  const isPackaged = useCallback(async (): Promise<boolean> => {
    if (!isElectron()) {
      // Not in Electron, so definitely not packaged
      return false;
    }

    try {
      return await window.electronAPI.app.isPackaged();
    } catch (error) {
      console.error('Failed to check if app is packaged:', error);

      return false;
    }
  }, [isElectron]);

  const showOpenDialog = useCallback(
    async (
      options: DialogOptions,
    ): Promise<{ canceled: boolean; filePaths: string[] }> => {
      if (!isElectron()) {
        console.log('Mock: Show open dialog with options:', options);

        return { canceled: true, filePaths: [] };
      }

      try {
        return await window.electronAPI.fs.showOpenDialog(options);
      } catch (error) {
        console.error('Failed to show open dialog:', error);

        return { canceled: true, filePaths: [] };
      }
    },
    [isElectron],
  );

  const showSaveDialog = useCallback(
    async (
      options: DialogOptions,
    ): Promise<{ canceled: boolean; filePath?: string }> => {
      if (!isElectron()) {
        console.log('Mock: Show save dialog with options:', options);

        return { canceled: true };
      }

      try {
        return await window.electronAPI.fs.showSaveDialog(options);
      } catch (error) {
        console.error('Failed to show save dialog:', error);

        return { canceled: true };
      }
    },
    [isElectron],
  );

  const readFile = useCallback(
    async (path: string): Promise<FileReadResponse> => {
      if (!isElectron()) {
        console.log('Mock: Reading file:', path);

        // Mock file read for browser development
        return {
          success: false,
          data: { content: '' },
          error: 'Not in Electron environment',
        };
      }

      try {
        const result = await window.electronAPI.fs.readFile(path);

        if (typeof result === 'string') {
          return { success: true, data: { content: result } };
        }

        return result as FileReadResponse;
      } catch (error) {
        console.error('Failed to read file:', error);

        return { success: false, data: { content: '' }, error: String(error) };
      }
    },
    [isElectron],
  );

  const readFileBase64 = useCallback(
    async (path: string): Promise<FileReadResponse> => {
      if (!isElectron()) {
        return {
          success: false,
          data: { content: '' },
          error: 'Not in Electron environment',
        };
      }

      try {
        const result = await window.electronAPI.fs.readFileBase64(path);

        if (typeof result === 'string') {
          return { success: true, data: { content: result } };
        }

        return result as FileReadResponse;
      } catch (error) {
        return { success: false, data: { content: '' }, error: String(error) };
      }
    },
    [isElectron],
  );

  const writeFile = useCallback(
    async (path: string, data: string): Promise<boolean> => {
      if (!isElectron()) {
        console.log(
          'Mock: Writing file:',
          path,
          'with data length:',
          data.length,
        );

        // Mock file write for browser development
        return true;
      }

      try {
        await window.electronAPI.fs.writeFile(path, data);

        return true;
      } catch (error) {
        console.error('Failed to write file:', error);

        return false;
      }
    },
    [isElectron],
  );

  // Certificate Management APIs
  const getCACertificateInfo =
    useCallback(async (): Promise<CertificateInfoResponse> => {
      if (!isElectron()) {
        console.log('Mock: Getting CA certificate info');

        return {
          success: true,
          data: {
            caInfo: {
              subject: 'Mock CA Certificate',
              issuer: 'Mock CA Certificate Authority',
              validFrom: new Date(),
              validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
              fingerprint: 'mock-fingerprint',
            },
          },
        };
      }

      try {
        const result = await window.electronAPI.certificates.getCaInfo();

        return result;
      } catch (error) {
        console.error('Failed to get CA certificate info:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  const exportCACertificate = useCallback(
    async (format: 'pem' | 'der' = 'pem'): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Exporting CA certificate in format:', format);

        return { success: true };
      }

      try {
        const result = await window.electronAPI.certificates.exportCa(format);

        return result;
      } catch (error) {
        console.error('Failed to export CA certificate:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const installCACertificate =
    useCallback(async (): Promise<InstallCertificateResponse> => {
      if (!isElectron()) {
        console.log('Mock: Installing CA certificate');

        return { success: true, data: {} };
      }

      try {
        const result = await window.electronAPI.certificates.installCa();

        return result;
      } catch (error) {
        console.error('Failed to install CA certificate:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  const isCACertificateInstalled = useCallback(async (): Promise<
    ElectronAPIResponse<{ installed: boolean }>
  > => {
    if (!isElectron()) {
      return { success: true, data: { installed: false } };
    }

    try {
      const result = await window.electronAPI.certificates.isInstalled();

      return result;
    } catch (error) {
      console.error('Failed to check CA installation:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [isElectron]);

  const removeCACertificate =
    useCallback(async (): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Removing CA certificate');

        return { success: true };
      }

      try {
        const result = await window.electronAPI.certificates.removeCa();

        return result;
      } catch (error) {
        console.error('Failed to remove CA certificate:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  // System Proxy APIs
  const getSystemProxyStatus =
    useCallback(async (): Promise<SystemProxyStatusResponse> => {
      if (!isElectron()) {
        console.log('Mock: Getting system proxy status');

        return {
          success: true,
          data: {
            status: { enabled: false, host: undefined, port: undefined },
          },
        };
      }

      try {
        const result = await window.electronAPI.systemProxy.getStatus();

        return result;
      } catch (error) {
        console.error('Failed to get system proxy status:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  const configureSystemProxy = useCallback(
    async (config: {
      host: string;
      port: number;
      enabled: boolean;
    }): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Configuring system proxy:', config);

        return { success: true };
      }

      try {
        const result = await window.electronAPI.systemProxy.configure(config);

        return result;
      } catch (error) {
        console.error('Failed to configure system proxy:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const restoreSystemProxy =
    useCallback(async (): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Restoring system proxy');

        return { success: true };
      }

      try {
        const result = await window.electronAPI.systemProxy.restore();

        return result;
      } catch (error) {
        console.error('Failed to restore system proxy:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  // Browser Launcher APIs
  const getSupportedBrowsers =
    useCallback(async (): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Getting supported browsers');

        return { success: true, data: { browsers: ['chromium'] } };
      }

      try {
        const result = await window.electronAPI.browser.getSupported();

        return result;
      } catch (error) {
        console.error('Failed to get supported browsers:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  const launchChrome = useCallback(
    async (config: BrowserLaunchConfig): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Launching Chrome with config:', config);

        return { success: true, data: { instanceId: 'mock-chrome-instance' } };
      }

      try {
        const result = await window.electronAPI.browser.launchChrome(config);

        return result;
      } catch (error) {
        console.error('Failed to launch Chrome:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const closeBrowser = useCallback(
    async (instanceId: string): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Closing browser instance:', instanceId);

        return { success: true };
      }

      try {
        const result =
          await window.electronAPI.browser.closeBrowser(instanceId);

        return result;
      } catch (error) {
        console.error('Failed to close browser:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const getBrowserInstances =
    useCallback(async (): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Getting browser instances');

        return { success: true, data: { instances: {} } };
      }

      try {
        const result = await window.electronAPI.browser.getBrowsers();

        return result;
      } catch (error) {
        console.error('Failed to get browser instances:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    }, [isElectron]);

  const injectCredentials = useCallback(
    async (
      config: BrowserInjectCredentialsConfig,
    ): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Injecting credentials:', config);

        return { success: true };
      }

      try {
        const result =
          await window.electronAPI.browser.injectCredentials(config);

        return result;
      } catch (error) {
        console.error('Failed to inject credentials:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  // Intercept Management APIs
  const resumeInterceptedRequest = useCallback(
    async (
      requestId: string,
      modifiedRequest?: ModifiedRequest,
    ): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Resuming intercepted request:', requestId);

        return { success: true };
      }

      try {
        const result = await window.electronAPI.intercept.resumeRequest(
          requestId,
          modifiedRequest,
        );

        return result;
      } catch (error) {
        console.error('Failed to resume intercepted request:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const dropInterceptedRequest = useCallback(
    async (requestId: string): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Dropping intercepted request:', requestId);

        return { success: true };
      }

      try {
        const result =
          await window.electronAPI.intercept.dropRequest(requestId);

        return result;
      } catch (error) {
        console.error('Failed to drop intercepted request:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const resumeInterceptedResponse = useCallback(
    async (
      responseId: string,
      modifiedResponse?: Partial<{
        statusCode: number;
        statusMessage: string;
        headers: Record<string, string | string[]>;
        body: string;
      }>,
    ): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Resuming intercepted response:', responseId);

        return { success: true };
      }

      try {
        const result = await window.electronAPI.intercept.resumeResponse(
          responseId,
          modifiedResponse,
        );

        return result;
      } catch (error) {
        console.error('Failed to resume intercepted response:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  const dropInterceptedResponse = useCallback(
    async (responseId: string): Promise<ElectronAPIResponse> => {
      if (!isElectron()) {
        console.log('Mock: Dropping intercepted response:', responseId);

        return { success: true };
      }

      try {
        const result =
          await window.electronAPI.intercept.dropResponse(responseId);

        return result;
      } catch (error) {
        console.error('Failed to drop intercepted response:', error);

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [isElectron],
  );

  return {
    // Proxy operations
    startProxy,
    stopProxy,
    getProxyStatus,
    toggleIntercept,
    setMtlsConfig,
    bindInstanceMtls,
    unbindInstanceMtls,
    setupTunInterface,

    // Request operations
    getRequests,
    clearRequests,
    exportRequests,

    // Communication
    sendMessage,
    addEventListener,
    removeEventListener,

    // App info
    getAppInfo,

    // File operations
    showOpenDialog,
    showSaveDialog,
    readFile,
    readFileBase64,
    writeFile,

    // Certificate management
    getCACertificateInfo,
    exportCACertificate,
    installCACertificate,
    isCACertificateInstalled,
    removeCACertificate,

    // System proxy management
    getSystemProxyStatus,
    configureSystemProxy,
    restoreSystemProxy,

    // Browser launcher
    getSupportedBrowsers,
    launchChrome,
    closeBrowser,
    getBrowserInstances,
    injectCredentials,

    // Intercept management
    resumeInterceptedRequest,
    dropInterceptedRequest,
    resumeInterceptedResponse,
    dropInterceptedResponse,

    // Utilities
    isElectron,
    isPackaged,
  };
};
